// Pure geometry helpers for marker/zone spatial queries.
//
// Extracted out of ModelViewer.vue so the math has a single home, is unit-testable, and
// the viewer stops owning business logic. These functions take the marker list (and the
// target) explicitly — no component `this`. They read each marker's live three.js
// `meshGroup` for world-space transforms, exactly as the original viewer code did.

import * as THREE from 'three';
import { OBB } from 'three/examples/jsm/math/OBB.js';
import InspectionAreaMarker from '@/domain/marker/InspectionAreaMarker';
import AnomalyMarker from '@/domain/marker/AnomalyMarker';
import HotSpotMarker from '@/domain/marker/HotSpotMarker';
import CreateHotSpotModel from '@/domain/marker/CreateHotSpotModel';

// Build an oriented bounding box from an inspection area's stored transform (scan-local space).
// Multi-scan annotations are compared within their own scan, so all spatial queries operate in
// that scan's local space — this builds the OBB from the data transform rather than the live
// (world) mesh group, so it works for not-yet-rendered request models too.
export function createLocalAreaObb(area) {
  const t = area.transform;
  const center = new THREE.Vector3(t.position.x, t.position.y, t.position.z);
  const rotation4 = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(t.rotation.x, t.rotation.y, t.rotation.z, t.rotation.order || 'XYZ')
  );
  const rotation3 = new THREE.Matrix3().setFromMatrix4(rotation4);
  const halfSize = new THREE.Vector3(
    Math.abs(t.scale.x) / 2,
    Math.abs(t.scale.y) / 2,
    Math.abs(t.scale.z) / 2
  );
  return new OBB(center, halfSize, rotation3);
}

// Build an oriented bounding box from an inspection-area marker's live mesh group (world space).
export function createAreaObb(area) {
  area.meshGroup.updateMatrixWorld(true);

  const center = area.meshGroup.getWorldPosition(new THREE.Vector3());

  const rotation4 = new THREE.Matrix4().extractRotation(area.meshGroup.matrixWorld);
  const rotation3 = new THREE.Matrix3().setFromMatrix4(rotation4);

  const halfSize = new THREE.Vector3(
    Math.abs(area.meshGroup.scale.x) / 2,
    Math.abs(area.meshGroup.scale.y) / 2,
    Math.abs(area.meshGroup.scale.z) / 2
  );

  return new OBB(center, halfSize, rotation3);
}

// Returns the id of the inspection area containing `point`, or null if none. `point` and the
// areas are compared in scan-local space; pass `scanId` to restrict to that scan's areas.
export function findInspectionAreaForPoint(point, markers, scanId = null) {
  let foundAreaId = null;

  markers.forEach(m => {
    if (!(m instanceof InspectionAreaMarker)) return;
    if (scanId != null && m.scanId !== scanId) return;

    const boxMatrix = new THREE.Matrix4();
    const boxPosition = new THREE.Vector3(m.meshGroup.position.x, m.meshGroup.position.y, m.meshGroup.position.z);
    const boxScale = new THREE.Vector3(m.meshGroup.scale.x, m.meshGroup.scale.y, m.meshGroup.scale.z);
    const boxRotation = new THREE.Euler(m.meshGroup.rotation._x, m.meshGroup.rotation._y, m.meshGroup.rotation._z, m.meshGroup.rotation._order);

    boxMatrix.compose(
      boxPosition,
      new THREE.Quaternion().setFromEuler(boxRotation),
      boxScale
    );

    const inverseBoxMatrix = new THREE.Matrix4().copy(boxMatrix).invert();
    const localPoint = new THREE.Vector3(point.x, point.y, point.z).applyMatrix4(inverseBoxMatrix);

    if (Math.abs(localPoint.x) <= 0.5 && Math.abs(localPoint.y) <= 0.5 && Math.abs(localPoint.z) <= 0.5) {
      foundAreaId = m.id;
    }
  });

  return foundAreaId;
}

// True if `marker`'s box intersects any OTHER inspection area's box (within the same scan).
// Uses scan-local OBBs so it works for a not-yet-rendered request model.
export function inspectionAreaHasConflicts(marker, markers, scanId = null) {
  const markerObb = createLocalAreaObb(marker);

  for (const area of markers) {
    if (area.id === marker.id) continue;
    if (!(area instanceof InspectionAreaMarker)) continue;
    if (scanId != null && area.scanId !== scanId) continue;

    const areaObb = createLocalAreaObb(area);
    if (markerObb.intersectsOBB(areaObb)) {
      return true;
    }
  }

  return false;
}

// Ids of markers whose (data) position falls within the hotspot's radius sphere. Positions are
// scan-local; pass `scanId` to restrict to markers on the same scan as the hotspot.
export function findMarkersInsideHotSpot(newHotSpot, markers, scanId = null) {
  if (!(newHotSpot instanceof CreateHotSpotModel) && !(newHotSpot instanceof HotSpotMarker)) return [];

  const center = new THREE.Vector3(
    newHotSpot.transform.position.x,
    newHotSpot.transform.position.y,
    newHotSpot.transform.position.z
  );

  const radius = Math.max(
    newHotSpot.transform.scale.x,
    newHotSpot.transform.scale.y,
    newHotSpot.transform.scale.z
  );

  const radiusSq = radius * radius;

  return markers
    .filter(marker => {
      if (marker.id === newHotSpot.id) return false;
      if (scanId != null && marker.scanId !== scanId) return false;

      const markerPosition = new THREE.Vector3(
        marker.transform.position.x,
        marker.transform.position.y,
        marker.transform.position.z
      );

      return markerPosition.distanceToSquared(center) <= radiusSq;
    })
    .map(marker => marker.id);
}

// Anomaly/hotspot ids whose (data) position is contained by the area's OBB. Compared in scan-local
// space; pass `scanId` to restrict to items on the same scan as the area.
export function findEncompassingMarkers(marker, markers, scanId = null) {
  const areaObb = createLocalAreaObb(marker);

  const markerIds = [];
  const hotSpotIds = [];

  for (const item of markers) {
    if (!(item instanceof AnomalyMarker) && !(item instanceof HotSpotMarker)) continue;
    if (scanId != null && item.scanId !== scanId) continue;

    const pos = new THREE.Vector3(item.transform.position.x, item.transform.position.y, item.transform.position.z);
    if (!areaObb.containsPoint(pos)) continue;

    if (item instanceof AnomalyMarker) {
      markerIds.push(item.id);
    } else {
      hotSpotIds.push(item.id);
    }
  }

  return { markerIds, hotSpotIds };
}
